import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getPriceTypeLabel, priceTypeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateVehicleTypeMutation, useUpdateVehicleTypeMutation } from '../../../../hooks/mutations/useVehicleTypeMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { VehicleType } from '../../../../services/apiSLTP/materials/vehiclesTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    vehicleType: VehicleType | null;
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateVehicleTypeModal: React.FC<ModalProps> = ({ vehicleType, showEditModal, setShowEditModal, isEditMode = true, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const [priceType, setPriceType] = useState({
        label: capitalizeFirstLetter(t(getPriceTypeLabel(vehicleType?.priceType || priceTypeEnum.DAY))),
        value: (vehicleType?.priceType || priceTypeEnum.DAY).toString(),
    });

    const [selectedDesignation, setSelectedDesignation] = useState(vehicleType?.designation || '');
    const [price, setPrice] = useState(typeof vehicleType?.price === 'number' ? vehicleType?.price.toString() : '');
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(vehicleType?.crudStatus || crudStatusEnum.OK);
    const [displayOrder, setDisplayOrder] = useState(typeof vehicleType?.displayOrder === 'number' ? vehicleType?.displayOrder.toString() : '');

    const { mutate: mutateVehicleType } = useUpdateVehicleTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating vehicleType:', error.message),
    });

    const { mutate: addVehicleType } = useCreateVehicleTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding vehicleType:', error.message),
    });

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    const isFormValid = () => {
        return !!selectedDesignation.length;
    };

    const handleSubmit = () => {
        if (isEditMode) {
            mutateVehicleType({
                vehicleTypeId: vehicleType?._id || '',
                data: {
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                },
            });
        } else {
            if (priceType)
                addVehicleType({
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                });
        }
    };

    return (
        <ModalBody
            title={t('vehicle')}
            subtitle={isEditMode ? t('update') : t('add')}
            disableSubmit={!isFormValid()}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={selectedDesignation} onChange={setSelectedDesignation} label={t('name')} errorText={t('error')} required />
            <ToggleLine checked={selectedCrudStatus === crudStatusEnum.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                required
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    if (value) setPriceType({ value: value?.value, label: value?.label });
                }}
                inputLabel={t('pricing')}
                required
            />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateVehicleTypeModal;
